import { RawTelemetryData } from '../domain/telemetry/raw/RawTelemetryData'
import { GpsReading } from '../domain/telemetry/raw/GpsReading'
import { AccelerometerReading } from '../domain/telemetry/raw/AccelerometerReading'
import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { ProcessedTelemetryData } from '../domain/telemetry/processed/ProcessedTelemetryData'
import { TelemetryStatistics } from '../domain/telemetry/processed/TelemetryStatistics'
import { TelemetryDiagnostics } from '../domain/telemetry/processed/TelemetryDiagnostics'
import { LeanAnglePoint } from '../domain/telemetry/processed/LeanAngle'
import { AccelerationPoint } from '../domain/telemetry/processed/Acceleration'
import { GForcePoint } from '../domain/telemetry/processed/GForce'
import { calculateDistance } from '../utils/distanceUtils'
import {
  formatElapsedTime,
  calculateSamplingFrequencyHz,
  getFrequencyAdaptiveConfig,
} from '../utils/timeUtils'
import { FrequencyAdaptiveConfig } from '../utils/FrequencyAdaptiveConfig'
import {
  smoothValues,
  removeSpeedSpikes,
  sanitizeSupraPhysicalSpeeds,
} from '../utils/smoothingUtils'
import { sustainedPeakAbs } from '../utils/sustainedPeak'
import { LEAN_PEAK_SUSTAIN_WINDOW_S } from '../analysis/config/leanDisplay'
import { IMU_VIBRATION_MAX_P95_JERK_MS3 } from '../analysis/config/imuVibrationGate'
import { classifyImuVibration } from './gpsRateAccelReduction'
import { calculateLeanAngleFromGps as calculateGpsBasedLeanAngle } from './gpsLeanAngleCalculator'
import { calculateAccelerationFromGps } from './gpsAccelerationCalculator'
import { calculateLeanAngleWithSensorFusion } from './sensorFusionLeanCalculator'
import { reduceReadingsToGpsTicks } from './imuWindowAverage'
import { calculateAccelerationWithSensorFusion } from './sensorFusionAccelerationCalculator'
import { validateAccelConsistency } from './accelConsistencyValidator'
import {
  estimateSensorFrameCalibration,
  applySensorFrameCalibration,
} from './sensorFrameCalibration'
import {
  classifyVehicleLeanRegime,
  computeGpsCoordinatedLeanSeries,
} from './gpsCoordinatedLean'
import { estimateImuLag, estimateImuLagDrift, shiftTimestamps } from './imuLag'
import { estimateGyroLeanRateBiasDegS } from './gyroBias'
import { KMH_PER_MPS } from '../utils/speedUnits'
import { MS_PER_S } from '../utils/timeUnits'
import { maxOf, minOf } from '../utils/extremes'

export interface TelemetryProcessorOptions {
  /** Calculate lean angle (default: true) */
  calculateLeanAngle?: boolean
  /** Calculate acceleration (default: true) */
  calculateAcceleration?: boolean
  /** Calculate g-force (default: true) */
  calculateGForce?: boolean
  /** Smoothing window size (optional) */
  smoothingWindow?: number
  /**
   * IMU-to-GPS clock offset measured over the whole recording session, ms,
   * positive = IMU behind GPS. A lap processed on its own applies it instead
   * of re-measuring over its short span.
   */
  imuLagMs?: number
  /**
   * Constant lean-rate offset of the calibrated gyro measured over the
   * whole recording session, deg/s (left positive). A lap processed on its
   * own removes it instead of re-measuring over its short span.
   */
  gyroLeanRateBiasDegS?: number
}

/**
 * Process raw telemetry data into fully processed telemetry with all metrics calculated.
 *
 * This ensures all required fields (leanAngle, acceleration, gForce) are always populated.
 *
 * @param rawTelemetry - Raw sensor readings from device
 * @param sessionStartTime - Session start time for elapsed time calculation
 * @param options - Processing options (all calculations enabled by default)
 * @returns Fully processed telemetry data
 */
export function processRawTelemetry(
  rawTelemetry: RawTelemetryData,
  sessionStartTime: Date,
  options: TelemetryProcessorOptions = {}
): ProcessedTelemetryData {
  if (!rawTelemetry.gps || rawTelemetry.gps.length === 0) {
    throw new Error('GPS data is required for telemetry processing')
  }

  // Default all calculations to true
  const enableLeanAngle = options.calculateLeanAngle !== false
  const enableAcceleration = options.calculateAcceleration !== false
  const enableGForce = options.calculateGForce !== false

  // Step 1: Convert GPS readings to processed points
  const gpsPoints = processGpsData(rawTelemetry.gps, sessionStartTime)

  // Calculate GPS frequency ONCE - used by all frequency-adaptive algorithms
  const frequencyHz = calculateSamplingFrequencyHz(gpsPoints)
  const frequencyConfig = getFrequencyAdaptiveConfig(frequencyHz)

  // Apply speed smoothing for high-frequency GPS data
  applySpeedSmoothing(gpsPoints, frequencyConfig)

  const alignment = alignImuToGps(
    enableLeanAngle || enableAcceleration
      ? withoutVibrationCorruptedImu(rawTelemetry, gpsPoints)
      : rawTelemetry,
    gpsPoints,
    options.imuLagMs,
    options.gyroLeanRateBiasDegS
  )
  const telemetry = alignment.telemetry

  // Step 2: Calculate statistics (updated later with derived metrics)
  const statistics = calculateTelemetryStatistics(gpsPoints, frequencyHz)

  // Step 3: Calculate lean angle (needed for gravity-corrected acceleration)
  const leanAngle = calculateLeanAngle(
    telemetry,
    gpsPoints,
    enableLeanAngle,
    alignment.diagnostics.gyroLeanRateBiasDegS ?? 0
  )
  if (leanAngle.length > 0 && enableLeanAngle) {
    // Headline peak = sustained (rolling-median) value, not the sample max —
    // see LEAN_PEAK_SUSTAIN_WINDOW_S. The lean series itself is untouched.
    statistics.maxLeanAngle = sustainedPeakAbs(
      leanAngle.map((p) => p.leanAngleDegrees),
      leanAngle.map((p) => p.timestamp.getTime()),
      LEAN_PEAK_SUSTAIN_WINDOW_S * MS_PER_S
    )
  }

  // Step 4: Calculate acceleration (uses lean angle for gravity correction)
  const acceleration = calculateAcceleration(
    telemetry,
    gpsPoints,
    leanAngle,
    frequencyConfig,
    enableAcceleration
  )
  if (acceleration.length > 0 && enableAcceleration) {
    const consistencyFlags = validateAccelConsistency(gpsPoints, acceleration)
    acceleration.forEach((p, i) => {
      p.speedTrendMismatch = consistencyFlags[i]
    })

    const longitudinalValues = acceleration.map((p) => p.longitudinalMs2)
    statistics.maxAccelerationMs2 = Math.max(
      maxOf(longitudinalValues.filter((v) => v > 0)),
      0
    )
    statistics.maxBrakingMs2 = Math.abs(
      Math.min(minOf(longitudinalValues.filter((v) => v < 0)), 0)
    )
  }

  // Step 5: Calculate g-force from acceleration
  const gForce = calculateGForce(acceleration, gpsPoints, enableGForce)
  if (gForce.length > 0 && enableGForce) {
    statistics.maxLongitudinalG = maxOf(
      gForce.map((p) => Math.abs(p.longitudinalG))
    )
    statistics.maxLateralG = maxOf(gForce.map((p) => Math.abs(p.lateralG)))
    statistics.maxCombinedG = maxOf(gForce.map((p) => p.combinedG))
  }

  return {
    gpsPoints,
    leanAngle,
    acceleration,
    gForce,
    statistics,
    diagnostics: {
      ...alignment.diagnostics,
      leanRegime: alignment.leanRegime,
      hangOffLeftDeg: null,
      hangOffRightDeg: null,
      leanSource: leanSourceFor(telemetry),
    },
  }
}

function processGpsData(
  gpsReadings: GpsReading[],
  sessionStartTime: Date
): GpsPoint[] {
  const points: GpsPoint[] = []
  let cumulativeDistance = 0

  for (let i = 0; i < gpsReadings.length; i++) {
    const reading = gpsReadings[i]

    // Preserve original distance if provided (e.g., from device file)
    // Otherwise, calculate distance from GPS coordinates
    if (reading.distanceMeters !== undefined) {
      cumulativeDistance = reading.distanceMeters
    } else if (i > 0) {
      const prevReading = gpsReadings[i - 1]
      const segmentDistance = calculateDistance(
        prevReading.latitude,
        prevReading.longitude,
        reading.latitude,
        reading.longitude
      )
      cumulativeDistance += segmentDistance
    }

    const speedMs =
      reading.speedMs !== undefined
        ? reading.speedMs
        : positionDerivedSpeedMs(gpsReadings, i)
    const speedKmh = speedMs * KMH_PER_MPS
    const elapsedMs = reading.timestamp.getTime() - sessionStartTime.getTime()

    points.push({
      latitude: reading.latitude,
      longitude: reading.longitude,
      altitude: reading.altitude,
      speedKmh,
      timestamp: reading.timestamp,
      distanceMeters: cumulativeDistance,
      elapsedMs,
      timeString: formatElapsedTime(elapsedMs),
    })
  }

  return points
}

function positionDerivedSpeedMs(readings: GpsReading[], index: number): number {
  const previous = Math.max(0, index - 1)
  const next = Math.min(readings.length - 1, index + 1)
  const secondsBetweenNeighbours =
    (readings[next].timestamp.getTime() -
      readings[previous].timestamp.getTime()) /
    MS_PER_S
  if (secondsBetweenNeighbours <= 0) return 0

  let pathMetersBetweenNeighbours = 0
  for (let i = previous; i < next; i++) {
    pathMetersBetweenNeighbours += calculateDistance(
      readings[i].latitude,
      readings[i].longitude,
      readings[i + 1].latitude,
      readings[i + 1].longitude
    )
  }
  return pathMetersBetweenNeighbours / secondsBetweenNeighbours
}

/**
 * Apply speed sanitization, spike removal and optional smoothing for GPS data.
 *
 * 1. Always rejects supra-physical speed samples (multi-sample receiver
 *    dropouts whose implied |dv/dt| is beyond physical plausibility — these
 *    defeat the local-median despiker and would poison the GPS speed trend,
 *    the pitch estimator and the accel validator downstream).
 * 2. Always removes GPS speed spikes (single-point outliers from bad GPS
 *    fixes) that would cause unrealistic acceleration/G-force values.
 * 3. For high-frequency GPS (>15Hz), additionally applies moving-average
 *    smoothing to reduce general noise.
 *
 * Mutates gpsPoints array in place.
 */
function applySpeedSmoothing(
  gpsPoints: GpsPoint[],
  frequencyConfig: FrequencyAdaptiveConfig
): void {
  if (gpsPoints.length <= 10) {
    return
  }

  // Step 1: Reject supra-physical speed samples (glitch runs) BEFORE the
  // median despiker — a dropout run corrupts the local median itself.
  const rawSpeeds = gpsPoints.map((p) => p.speedKmh)
  const timestampsMs = gpsPoints.map((p) => p.timestamp.getTime())
  const sanitized = sanitizeSupraPhysicalSpeeds(rawSpeeds, timestampsMs)
  if (sanitized !== rawSpeeds) {
    for (let i = 0; i < gpsPoints.length; i++) {
      if (sanitized[i] !== rawSpeeds[i]) {
        gpsPoints[i] = { ...gpsPoints[i], speedKmh: sanitized[i] }
      }
    }
  }

  // Step 2: Remove speed spikes at all frequencies.
  // GPS speed spikes (e.g., a single reading jumping 50+ km/h) are common even
  // at 10 Hz and cause unrealistic peak G values when differentiated.
  const speeds = gpsPoints.map((p) => p.speedKmh)
  const despiked = removeSpeedSpikes(speeds)

  for (let i = 0; i < gpsPoints.length; i++) {
    if (despiked[i] !== speeds[i]) {
      gpsPoints[i] = { ...gpsPoints[i], speedKmh: despiked[i] }
    }
  }

  // Step 3: Additional moving-average smoothing for high-frequency data (>15 Hz)
  if (frequencyConfig.frequencyHz <= 15) {
    return
  }

  // Scale smoothing window based on frequency - aim for ~0.3 seconds of smoothing
  const windowSize = Math.round(3 * frequencyConfig.frequencyScale)

  const currentSpeeds = gpsPoints.map((p) => p.speedKmh)
  const smoothedSpeeds = smoothValues(currentSpeeds, windowSize)

  for (let i = 0; i < gpsPoints.length; i++) {
    gpsPoints[i] = { ...gpsPoints[i], speedKmh: smoothedSpeeds[i] }
  }
}

function calculateTelemetryStatistics(
  points: GpsPoint[],
  frequencyHz: number
): TelemetryStatistics {
  if (points.length === 0) {
    return {
      maxSpeedKmh: 0,
      avgSpeedKmh: 0,
      totalDistanceMeters: 0,
      totalDurationMs: 0,
      dataPointCount: 0,
      samplingFrequencyHz: 0,
      maxLeanAngle: 0,
      maxLongitudinalG: 0,
      maxLateralG: 0,
      maxCombinedG: 0,
      maxAccelerationMs2: 0,
      maxBrakingMs2: 0,
    }
  }

  const speeds = points.map((p) => p.speedKmh)
  const maxSpeedKmh = maxOf(speeds)
  const avgSpeedKmh = speeds.reduce((sum, s) => sum + s, 0) / speeds.length
  const totalDistanceMeters = points[points.length - 1].distanceMeters
  const totalDurationMs = points[points.length - 1].elapsedMs

  return {
    maxSpeedKmh: Math.round(maxSpeedKmh * 100) / 100,
    avgSpeedKmh: Math.round(avgSpeedKmh * 100) / 100,
    totalDistanceMeters: Math.round(totalDistanceMeters),
    totalDurationMs,
    dataPointCount: points.length,
    samplingFrequencyHz: Math.round(frequencyHz * 10) / 10,
    // Populated later after calculating derived metrics
    maxLeanAngle: 0,
    maxLongitudinalG: 0,
    maxLateralG: 0,
    maxCombinedG: 0,
    maxAccelerationMs2: 0,
    maxBrakingMs2: 0,
  }
}

function calculateLeanAngle(
  rawTelemetry: RawTelemetryData,
  gpsPoints: GpsPoint[],
  enabled: boolean,
  leanRateBiasDegS: number
): LeanAnglePoint[] {
  if (!enabled) {
    // Return zero-initialized array
    return gpsPoints.map((point) => ({
      leanAngleDegrees: 0,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
      speedKmh: point.speedKmh,
      source: 'calculated-gps' as const,
    }))
  }

  // Priority 1: Full sensor fusion (accelerometer + gyroscope + GPS)
  // Most accurate: combines fast gyro response, absolute accel reference, and GPS validation
  if (rawTelemetry.accelerometer && rawTelemetry.gyroscope) {
    return calculateLeanAngleWithSensorFusion(
      gpsPoints,
      rawTelemetry.accelerometer,
      rawTelemetry.gyroscope,
      { leanRateBiasDegS }
    )
  }

  // Priority 2: Accelerometer only (direct gravity vector measurement)
  // Good accuracy: absolute reference, no drift, but misses fast transitions
  if (rawTelemetry.accelerometer) {
    return calculateLeanAngleFromAccelerometer(
      rawTelemetry.accelerometer,
      gpsPoints
    )
  }

  // Priority 3: GPS-only (derived from turn radius + speed)
  // Fallback: physics-based calculation when no IMU sensors available
  return calculateGpsBasedLeanAngle(gpsPoints)
}

/**
 * Calculate lean angle from accelerometer data.
 *
 * Uses the gravity vector measured by the accelerometer to determine the device's
 * roll angle (lean). This is a direct measurement of actual lean, more accurate
 * than GPS-derived lean which is calculated from turn radius.
 *
 * The roll angle is calculated as: θ = atan2(ay, √(ax² + az²))
 * where ay is the lateral acceleration and ax, az are forward/vertical.
 */
function calculateLeanAngleFromAccelerometer(
  accelerometer: AccelerometerReading[],
  gpsPoints: GpsPoint[]
): LeanAnglePoint[] {
  const perTick = reduceReadingsToGpsTicks(accelerometer, gpsPoints)
  return gpsPoints.map((point, i) => {
    const accelReading = perTick[i]
    if (!accelReading) {
      return {
        leanAngleDegrees: 0,
        timestamp: point.timestamp,
        distanceMeters: point.distanceMeters,
        elapsedMs: point.elapsedMs,
        speedKmh: point.speedKmh,
        source: 'accelerometer' as const,
      }
    }

    // Convert to m/s² if in g units
    const accelX =
      accelReading.unit === 'g' ? accelReading.x * 9.81 : accelReading.x
    const accelY =
      accelReading.unit === 'g' ? accelReading.y * 9.81 : accelReading.y
    const accelZ =
      accelReading.unit === 'g' ? accelReading.z * 9.81 : accelReading.z

    // Calculate roll angle from gravity vector
    // When upright: ay ≈ 0, az ≈ -9.81 (gravity pointing down)
    // When leaned: ay increases (gravity has lateral component)
    const roll = Math.atan2(
      accelY,
      Math.sqrt(accelX * accelX + accelZ * accelZ)
    )
    const leanAngleDegrees = (roll * 180) / Math.PI

    return {
      leanAngleDegrees: Math.round(leanAngleDegrees * 100) / 100,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
      speedKmh: point.speedKmh,
      source: 'accelerometer' as const,
    }
  })
}

function calculateAcceleration(
  rawTelemetry: RawTelemetryData,
  gpsPoints: GpsPoint[],
  leanAngle: LeanAnglePoint[],
  frequencyConfig: FrequencyAdaptiveConfig,
  enabled: boolean
): AccelerationPoint[] {
  if (!enabled) {
    // Return zero-initialized array
    return gpsPoints.map((point) => ({
      longitudinalMs2: 0,
      lateralMs2: 0,
      verticalMs2: 0,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
      source: 'calculated-gps' as const,
    }))
  }

  // Priority 1: Sensor fusion with 3D orientation (accelerometer + gyroscope + lean)
  // Most accurate: gyroscope tracks pitch (nose up/down) during braking/acceleration
  // Critical: at 10° pitch, gravity adds ~0.17g error to longitudinal reading
  if (rawTelemetry.accelerometer) {
    return calculateAccelerationWithSensorFusion(
      gpsPoints,
      rawTelemetry.accelerometer,
      leanAngle,
      frequencyConfig,
      rawTelemetry.gyroscope ?? [] // Pass gyroscope for full 3D orientation if available
    )
  }

  // Priority 2: GPS-derived acceleration (when no accelerometer)
  // Fallback: calculates from speed changes and turn radius
  return calculateAccelerationFromGps(gpsPoints, frequencyConfig)
}

function calculateGForce(
  acceleration: AccelerationPoint[],
  gpsPoints: GpsPoint[],
  enabled: boolean
): GForcePoint[] {
  if (!enabled) {
    // Return zero-initialized array
    return gpsPoints.map((point) => ({
      longitudinalG: 0,
      lateralG: 0,
      verticalG: 0,
      combinedG: 0,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
    }))
  }

  return acceleration.map((point) => {
    const longitudinalG = point.longitudinalMs2 / 9.81
    const lateralG = point.lateralMs2 / 9.81
    const verticalG = point.verticalMs2 / 9.81
    const combinedG = Math.sqrt(
      longitudinalG * longitudinalG +
        lateralG * lateralG +
        verticalG * verticalG
    )

    return {
      longitudinalG: Math.round(longitudinalG * 100) / 100,
      lateralG: Math.round(lateralG * 100) / 100,
      verticalG: Math.round(verticalG * 100) / 100,
      combinedG: Math.round(combinedG * 100) / 100,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
    }
  })
}

function withoutVibrationCorruptedImu(
  rawTelemetry: RawTelemetryData,
  gpsPoints: GpsPoint[]
): RawTelemetryData {
  if (!rawTelemetry.accelerometer) return rawTelemetry
  const vibration = classifyImuVibration(rawTelemetry.accelerometer, gpsPoints)
  if (vibration.verdict !== 'vibration-corrupted') return rawTelemetry
  console.warn(
    `[telemetry] IMU ignored: vibration-corrupted accelerometer ` +
      `(p95 jerk ${vibration.p95JerkMs3.toFixed(0)} m/s³ over ` +
      `${vibration.movingSampleCount} moving samples, limit ` +
      `${IMU_VIBRATION_MAX_P95_JERK_MS3}). Using GPS-derived lean and acceleration.`
  )
  return { ...rawTelemetry, accelerometer: undefined, gyroscope: undefined }
}

function leanSourceFor(
  telemetry: RawTelemetryData
): TelemetryDiagnostics['leanSource'] {
  if (telemetry.accelerometer && telemetry.gyroscope) return 'gps-anchored'
  if (telemetry.accelerometer) return 'accelerometer'
  return 'gps'
}

interface ImuAlignment {
  telemetry: RawTelemetryData
  leanRegime: TelemetryDiagnostics['leanRegime']
  diagnostics: Omit<
    TelemetryDiagnostics,
    'leanRegime' | 'hangOffLeftDeg' | 'hangOffRightDeg' | 'leanSource'
  >
}

function alignImuToGps(
  rawTelemetry: RawTelemetryData,
  gpsPoints: GpsPoint[],
  suppliedLagMs?: number,
  suppliedLeanRateBiasDegS?: number
): ImuAlignment {
  const untouched: ImuAlignment = {
    telemetry: rawTelemetry,
    leanRegime: 'unknown',
    diagnostics: {
      imuLagMs: null,
      imuLagCorrelation: null,
      imuLagDriftMs: null,
      calibrationTrusted: false,
      calibrationTiltDeg: 0,
      calibrationYawDeg: 0,
      yawRefinedByGyro: false,
      yawRefinementCorrelation: null,
      gyroLeanRateBiasDegS: null,
    },
  }
  const { accelerometer, gyroscope } = rawTelemetry
  if (
    !accelerometer ||
    !gyroscope ||
    accelerometer.length === 0 ||
    gyroscope.length === 0
  )
    return untouched

  const calibration = estimateSensorFrameCalibration(
    accelerometer,
    gpsPoints,
    gyroscope
  )
  const gyroTicks = reduceReadingsToGpsTicks(
    applySensorFrameCalibration(gyroscope, calibration),
    gpsPoints
  )
  const accelTicks = reduceReadingsToGpsTicks(
    applySensorFrameCalibration(accelerometer, calibration),
    gpsPoints
  )
  const gpsCoordinatedLean = computeGpsCoordinatedLeanSeries(gpsPoints)
  const leanRegime = classifyVehicleLeanRegime(
    gpsPoints,
    accelTicks.filter((a): a is AccelerometerReading => a !== undefined),
    gpsCoordinatedLean
  )
  const supplied =
    typeof suppliedLagMs === 'number' && Number.isFinite(suppliedLagMs)
      ? suppliedLagMs
      : null
  const lag = supplied !== null ? null : estimateImuLag(gpsPoints, gyroTicks)
  const drift =
    supplied !== null ? null : estimateImuLagDrift(gpsPoints, gyroTicks)
  const shiftMs = supplied ?? lag?.lagMs ?? 0
  const shifted =
    shiftMs === 0
      ? null
      : {
          accelerometer: shiftTimestamps(accelerometer, -shiftMs),
          gyroscope: shiftTimestamps(gyroscope, -shiftMs),
        }
  const telemetry = shifted ? { ...rawTelemetry, ...shifted } : rawTelemetry
  const shiftedCalibration = shifted
    ? estimateSensorFrameCalibration(
        shifted.accelerometer,
        gpsPoints,
        shifted.gyroscope
      )
    : calibration
  const leanRateBiasDegS =
    typeof suppliedLeanRateBiasDegS === 'number' &&
    Number.isFinite(suppliedLeanRateBiasDegS)
      ? suppliedLeanRateBiasDegS
      : estimateGyroLeanRateBiasDegS(
          gpsPoints,
          shifted
            ? reduceReadingsToGpsTicks(
                applySensorFrameCalibration(
                  shifted.gyroscope,
                  shiftedCalibration
                ),
                gpsPoints
              )
            : gyroTicks,
          gpsCoordinatedLean
        )
  return {
    telemetry,
    leanRegime,
    diagnostics: {
      imuLagMs: supplied ?? lag?.lagMs ?? null,
      imuLagCorrelation: supplied !== null ? null : lag?.correlation ?? null,
      imuLagDriftMs: lag && drift ? drift.lastMs - drift.firstMs : null,
      calibrationTrusted: shiftedCalibration.trusted,
      calibrationTiltDeg: shiftedCalibration.tiltDeg,
      calibrationYawDeg: shiftedCalibration.yawDeg,
      yawRefinedByGyro: shiftedCalibration.yawRefined,
      yawRefinementCorrelation: shiftedCalibration.yawRefinementCorrelation,
      gyroLeanRateBiasDegS: leanRateBiasDegS,
    },
  }
}
